'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { apiPost } from '@/lib/api';
import { useFormatName } from '@/contexts/NameFormattingContext';
import { Avatar } from '@/components/ui/Avatar';

export type PlayerOption = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  profile_photo: string | null;
  current_rating: number;
};

type Props = {
  federationId?: string;
  eventId?: string;
  value: string | null;
  onChange: (playerId: string | null, selected?: PlayerOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  initialOption?: Partial<PlayerOption> | null;
  allowClear?: boolean;
  /** Hide guest players (no auth user). Default false. */
  excludeGuests?: boolean;
  /**
   * Add entries: when set, a typed query ends the list with one more row that hands the text back
   * (`createGuestLabel` names it, e.g. 'Add "Verify Gue" as a guest'). Absent, nothing changes.
   */
  onCreateGuest?: (query: string) => void;
  createGuestLabel?: (query: string) => string;
  /** Right-hand note on the guest row, e.g. "no login · email optional". */
  createGuestHint?: string;
  /** A note after a match's meta, e.g. "already in Advanced"; null for none. */
  annotate?: (opt: PlayerOption) => string | null;
  /** A magnifier inside the box, left. */
  leadingIcon?: boolean;
  /** Focus the box on mount. */
  autoFocus?: boolean;
  /** Default true: focusing opens the list. False keeps it shut until something is typed. */
  openOnFocus?: boolean;
};

export default function PlayerSearchSelect({
  federationId,
  eventId,
  value,
  onChange,
  placeholder,
  disabled,
  initialOption,
  allowClear,
  excludeGuests,
  onCreateGuest,
  createGuestLabel,
  createGuestHint,
  annotate,
  leadingIcon = false,
  autoFocus = false,
  openOnFocus = true,
}: Props) {
  const t = useTranslations('UserSearch');
  const locale = useLocale();
  const formatName = useFormatName();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PlayerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const path = '/api/user/search';
        const payload = { search: query, limit: 20, federationId, eventId, excludeGuests };
        const data = (await apiPost<{ rows: PlayerOption[] }>(path, payload)) as {
          rows?: PlayerOption[];
        };
        if (!active) return;
        setOptions((data?.rows as PlayerOption[]) || []);
      } catch {
        if (!active) return;
        setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    const id = setTimeout(run, 200);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [eventId, federationId, excludeGuests, locale, query]);

  const selected = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);

  // The guest row: only with a callback and something typed, never for a blank or a chosen player.
  const guestQuery = onCreateGuest && !selected ? query.trim() : '';
  // The typed text reads bold inside its label ('Add **Verify Gue** as a guest').
  const guestLabel = () => {
    const label = createGuestLabel?.(guestQuery) ?? guestQuery;
    const at = label.indexOf(guestQuery);
    if (at < 0) return label;
    return (
      <>
        {label.slice(0, at)}
        <strong className="font-semibold">{guestQuery}</strong>
        {label.slice(at + guestQuery.length)}
      </>
    );
  };
  const guestRow = guestQuery ? (
    <li key="__guest">
      <button
        type="button"
        className="bg-warning/[0.06] hover:bg-warning/10 flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
        onClick={() => {
          onCreateGuest?.(guestQuery);
          setOpen(false);
        }}
      >
        <span
          className="border-warning text-warning flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-dashed text-xs font-bold"
          aria-hidden="true"
        >
          +
        </span>
        <span className="truncate">{guestLabel()}</span>
        {createGuestHint && (
          <span className="text-foreground/60 ml-auto flex-shrink-0 text-xs">{createGuestHint}</span>
        )}
      </button>
    </li>
  ) : null;

  const display = useMemo(() => {
    const opt = selected || (initialOption && value ? { ...initialOption, id: value } : null);
    if (!opt) return '';
    const name = formatName(opt.first_name ?? null, opt.last_name ?? null);
    return name || '';
  }, [initialOption, selected, value]);

  return (
    <div ref={containerRef} className="relative w-full">
      {leadingIcon && (
        <MagnifyingGlassIcon
          className="text-foreground/50 pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
          aria-hidden="true"
        />
      )}
      <input
        value={
          selected
            ? formatName(selected.first_name, selected.last_name)
            : open || query
              ? query
              : display
        }
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (openOnFocus || query) setOpen(true);
        }}
        placeholder={placeholder || t('searchPlaceholder')}
        disabled={disabled}
        autoFocus={autoFocus}
        className={`bg-background border-border text-foreground placeholder:text-foreground w-full rounded-md border py-1.5 pr-3 text-sm ${leadingIcon ? 'pl-9' : 'pl-3'}`}
      />
      {allowClear && !disabled && value ? (
        <button
          type="button"
          aria-label="Clear"
          className="text-foreground/60 hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 rounded px-1"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setQuery('');
            setOpen(false);
            onChange(null, null);
          }}
        >
          ×
        </button>
      ) : null}
      {open && (
        <div className="bg-background border-border absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-md border shadow-lg">
          {loading ? (
            <div className="text-foreground/70 px-3 py-2 text-sm">…</div>
          ) : options.length === 0 && !guestRow ? (
            <div className="text-foreground/70 px-3 py-2 text-sm">{t('noResults')}</div>
          ) : (
            <ul className="divide-y">
              {options.map((opt) => {
                const name = formatName(opt.first_name, opt.last_name) || '—';
                const rating = opt.current_rating?.toFixed?.(0) ?? opt.current_rating;
                const uname = opt.username ? `@${opt.username}` : '';
                const meta = [uname, rating, annotate?.(opt)].filter(Boolean).join(' · ');
                return (
                  <li key={opt.id}>
                    <button
                      type="button"
                      className="hover:bg-surface flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm"
                      onClick={() => {
                        onChange(opt.id, opt);
                        setOpen(false);
                      }}
                    >
                      <Avatar name={name} photoUrl={opt.profile_photo} size="sm" />
                      <span className="truncate font-medium">{name}</span>
                      <span className="text-foreground/70 ml-auto flex-shrink-0 text-xs">{meta}</span>
                    </button>
                  </li>
                );
              })}
              {guestRow}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
